/**
 * EncounterCard
 *
 * Mobile card view for displaying individual encounters.
 * Enterprise design with left border color-coded by status,
 * stronger visual hierarchy, and touch-friendly action buttons.
 */

import React from 'react';
import { format } from 'date-fns';
import { EHR_PRE_LOCALIZED_SYSTEMS, formatEhrTime, formatEhrDate } from '@/utils/dateFormat';
import {
  EyeIcon,
  PlayIcon,
  DocumentTextIcon,
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { UnifiedEncounterItem } from '../types';

// ============================================================================
// Status Configuration
// ============================================================================

const STATUS_BORDER_COLORS: Record<string, string> = {
  completed: 'border-l-emerald-500',
  ready: 'border-l-emerald-500',
  recording: 'border-l-blue-500',
  'in-progress': 'border-l-blue-500',
  processing: 'border-l-amber-500',
  failed: 'border-l-red-500',
  cancelled: 'border-l-red-400',
  'no-show': 'border-l-orange-400',
  pending: 'border-l-gray-300',
  scheduled: 'border-l-blue-500',
  'sent-to-ehr': 'border-l-emerald-500',
  'failed-to-send': 'border-l-red-500',
};

const ENCOUNTER_STATUS_STYLES: Record<string, { dot: string; text: string; bg: string; pulse?: boolean }> = {
  completed: { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  ready: { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  recording: { dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', pulse: true },
  'in-progress': { dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', pulse: true },
  processing: { dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', pulse: true },
  failed: { dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' },
  cancelled: { dot: 'bg-red-400', text: 'text-red-600', bg: 'bg-red-50' },
  'no-show': { dot: 'bg-orange-400', text: 'text-orange-700', bg: 'bg-orange-50' },
  pending: { dot: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-100' },
  scheduled: { dot: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-100' },
};

// ============================================================================
// Status Badge Component
// ============================================================================

interface StatusBadgeProps {
  status: string;
  type: 'recording' | 'transcription' | 'visit' | 'match';
  size?: 'sm' | 'md';
}

function StatusBadge({ status, type, size = 'sm' }: StatusBadgeProps) {
  if (type === 'match') {
    const matchConfig: Record<string, { text: string; bg: string; Icon: typeof CheckCircleIcon }> = {
      matched: { text: 'text-emerald-700', bg: 'bg-emerald-50', Icon: CheckCircleIcon },
      unmatched: { text: 'text-red-700', bg: 'bg-red-50', Icon: ExclamationTriangleIcon },
      pending: { text: 'text-amber-700', bg: 'bg-amber-50', Icon: ClockIcon },
    };
    const config = matchConfig[status];
    if (!config) return null;
    const { Icon, text, bg } = config;
    const iconClass = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
    const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs';
    return (
      <span className={`inline-flex items-center gap-1 rounded-full font-medium ${sizeClasses} ${bg} ${text}`}>
        <Icon className={iconClass} />
        {status.replace(/-/g, ' ')}
      </span>
    );
  }

  const config = ENCOUNTER_STATUS_STYLES[status] || ENCOUNTER_STATUS_STYLES.pending;
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs';

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-medium ${sizeClasses} ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot} ${config.pulse ? 'animate-pulse' : ''}`} />
      {status.replace(/-/g, ' ').replace(/_/g, ' ')}
    </span>
  );
}

// ============================================================================
// Main Card Component
// ============================================================================

interface EncounterCardProps {
  item: UnifiedEncounterItem;
  showMatchStatus?: boolean;
  showExternalSystem?: boolean;
  onViewDetails?: (item: UnifiedEncounterItem) => void;
  onStartRecording?: (item: UnifiedEncounterItem) => void;
  onViewTranscript?: (item: UnifiedEncounterItem) => void;
  onChangeProvider?: (item: UnifiedEncounterItem) => void;
  /** Show who the provider is (standalone); the Change action needs onChangeProvider too. */
  showProvider?: boolean;
}

export function EncounterCard({
  item,
  showMatchStatus = false,
  showExternalSystem = false,
  onViewDetails,
  onStartRecording,
  onViewTranscript,
  onChangeProvider,
  showProvider = false,
}: EncounterCardProps) {
  const borderColor = STATUS_BORDER_COLORS[item.status] || 'border-l-gray-300';
  // A cancelled or no-show standalone visit was never recorded: show the visit
  // status, as the desktop table does, not "Rec pending / Trans pending".
  const inactiveVisit = item._type === 'appointment' && (item.status === 'cancelled' || item.status === 'no-show');

  return (
    <div className={`bg-white border border-gray-200 border-l-4 ${borderColor} rounded-xl shadow-clinical-soft hover:shadow-clinical-medium transition-all duration-200`}>
      <div className="p-4">
        {/* Header: Patient name + status */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <UserIcon className="h-4 w-4 text-gray-500" />
              </div>
              <div className="min-w-0">
                <h4 className="text-base font-semibold text-gray-900 truncate">{item.patientName}</h4>
                {item.patientMrn && (
                  <span className="text-[11px] font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded inline-block mt-0.5">
                    MRN: {item.patientMrn}
                  </span>
                )}
              </div>
            </div>
          </div>
          {showMatchStatus && item.matchStatus && (
            <StatusBadge status={item.matchStatus} type="match" size="md" />
          )}
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="flex items-center gap-2 text-sm">
            <CalendarIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div>
              {item.externalSystem && EHR_PRE_LOCALIZED_SYSTEMS.has(item.externalSystem) ? (
                <>
                  <span className="font-medium text-gray-700">{formatEhrTime(item.scheduledDate)}</span>
                  <span className="text-gray-400 mx-1">&middot;</span>
                  <span className="text-gray-500">{formatEhrDate(item.scheduledDate)}</span>
                </>
              ) : (
                <>
                  <span className="font-medium text-gray-700">{format(item.scheduledDate, 'h:mm a')}</span>
                  <span className="text-gray-400 mx-1">&middot;</span>
                  <span className="text-gray-500">{format(item.scheduledDate, 'MMM d')}</span>
                </>
              )}
            </div>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-900 capitalize">{item.type}</span>
          </div>
          {item.location && (
            <div className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
              <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span>{item.location}</span>
            </div>
          )}
        </div>

        {/* Chief Complaint */}
        {item.chiefComplaint && (
          <div className="mb-3 pl-3 border-l-2 border-gray-200 py-1">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">Chief Complaint</p>
            <p className="text-sm text-gray-700 line-clamp-2">{item.chiefComplaint}</p>
          </div>
        )}

        {/* Status Row */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {inactiveVisit && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 uppercase tracking-wide">Visit</span>
              <StatusBadge status={item.status} type="visit" />
            </div>
          )}
          {!inactiveVisit && item.recordingStatus && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 uppercase tracking-wide">Rec</span>
              <StatusBadge status={item.recordingStatus} type="recording" />
            </div>
          )}
          {!inactiveVisit && item.transcriptionStatus && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 uppercase tracking-wide">Trans</span>
              <StatusBadge status={item.transcriptionStatus} type="transcription" />
            </div>
          )}
        </div>

        {/* External System Info */}
        {showExternalSystem && item.externalSystem && (
          <div className="text-xs text-gray-500 mb-3 pb-3 border-b border-gray-100 flex items-center gap-2">
            <span className="uppercase font-semibold tracking-wide text-gray-600">{item.externalSystem}</span>
            {item.externalId && (
              <span className="font-mono bg-gray-50 px-1.5 py-0.5 rounded truncate max-w-[140px]" title={item.externalId}>
                {item.externalId}
              </span>
            )}
          </div>
        )}

        {/* Provider, changeable by a standalone admin (AMP-137) */}
        {showProvider && (
          <div className="mb-3 flex items-center gap-2 text-sm">
            <span className="text-gray-500">Provider:</span>
            <span className="text-gray-800">{item.providerName || 'Unassigned'}</span>
            {onChangeProvider && item.recordingStatus === 'pending' && (item.transcriptionStatus ?? 'pending') === 'pending' && (
            <button
              type="button"
              className="ml-auto text-xs font-medium text-blue-700 hover:text-blue-900 min-h-[44px] px-2"
              aria-label={`Change provider for ${item.patientName}`}
              onClick={() => onChangeProvider(item)}
            >
              Change
            </button>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {onViewDetails && (
            <button
              type="button"
              onClick={() => onViewDetails(item)}
              className="flex-1 flex items-center justify-center gap-2 px-4 min-h-[44px] bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg font-medium text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
            >
              <EyeIcon className="h-5 w-5" />
              View Details
            </button>
          )}
          {item.hasTranscript && onViewTranscript && (
            <button
              type="button"
              onClick={() => onViewTranscript(item)}
              className="flex items-center justify-center gap-2 px-4 min-h-[44px] bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg font-medium text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500"
            >
              <DocumentTextIcon className="h-5 w-5" />
            </button>
          )}
          {item.canStartRecording && onStartRecording && (
            <button
              type="button"
              onClick={() => onStartRecording(item)}
              className="flex items-center justify-center gap-2 px-4 min-h-[44px] bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg font-medium text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
            >
              <PlayIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Card List Component
// ============================================================================

interface EncounterCardListProps {
  items: UnifiedEncounterItem[];
  showMatchStatus?: boolean;
  showExternalSystem?: boolean;
  onViewDetails?: (item: UnifiedEncounterItem) => void;
  onStartRecording?: (item: UnifiedEncounterItem) => void;
  onViewTranscript?: (item: UnifiedEncounterItem) => void;
  onChangeProvider?: (item: UnifiedEncounterItem) => void;
  showProvider?: boolean;
}

export function EncounterCardList({
  items,
  showMatchStatus,
  showExternalSystem,
  onViewDetails,
  onStartRecording,
  onViewTranscript,
  onChangeProvider,
  showProvider,
}: EncounterCardListProps) {
  return (
    <div className="space-y-3">
      {items.map((item) => (
        <EncounterCard
          key={item.id}
          item={item}
          showMatchStatus={showMatchStatus}
          showExternalSystem={showExternalSystem}
          onViewDetails={onViewDetails}
          onStartRecording={onStartRecording}
          onViewTranscript={onViewTranscript}
          onChangeProvider={onChangeProvider}
          showProvider={showProvider}
        />
      ))}
    </div>
  );
}

export default EncounterCard;
